import type { SearchEntityType } from "../../services/apis/search.api";

export interface AuthLike {
    isAuthenticated?: boolean;
    adminMode?: boolean;
    ownerMode?: boolean;
    superAdminMode?: boolean;
    p3mSuperAdminMode?: boolean;
    user?: {
        _id?: string;
        role?: string[];
        adminFeaturesAllowed?: string[];
        organization?: any[];
    } | null;
}

export interface FeatureFlags {
    imageVerification?: boolean;
}

/**
 * Returns the entity types the current user can see in search results.
 * Mirrors the route-protection and ProfileDropdown visibility rules.
 */
export function getSearchableEntityTypes(
    auth: AuthLike,
    flags: FeatureFlags = { imageVerification: true }
): SearchEntityType[] {
    if (!auth.isAuthenticated) return [];

    const roles = auth.user?.role || [];
    const isAdmin = !!auth.adminMode;
    const isOwner = !!auth.ownerMode;
    const isSuperAdmin = !!auth.superAdminMode;
    const isP3mSuperAdmin = !!auth.p3mSuperAdminMode;
    const isAuditor = roles.includes("Auditor");
    const isEvaluator = roles.includes("Evaluator");
    const adminFeatures = auth.user?.adminFeaturesAllowed || [];
    const canManageAuditee =
        isEvaluator && adminFeatures.includes("ManageAuditee");
    const anyAdmin = isAdmin || isSuperAdmin || isP3mSuperAdmin;

    const allowed: SearchEntityType[] = [];

    // Audits - any authenticated user
    allowed.push("audits");

    // Assessments - admin / super / p3m / evaluator
    if (anyAdmin || isEvaluator) allowed.push("assessments");

    // Evaluations - admin / super / p3m (AdminRoute)
    if (anyAdmin) allowed.push("evaluations");

    // Anecdotal records - admin / super / p3m (AdminRoute)
    if (anyAdmin) allowed.push("anecdotals");

    // Tasks - admin / super / p3m (AdminRoute)
    if (anyAdmin) allowed.push("tasks");

    // Image Verification - all authenticated users, subject to feature flag
    if (flags.imageVerification !== false) allowed.push("imageVerification");

    // People - admin / super / p3m / owner, or evaluator with ManageAuditee
    if (anyAdmin || isOwner || canManageAuditee) allowed.push("people");

    // Groups - any authenticated user
    allowed.push("groups");

    // Parameters - admin / super / p3m / auditor
    if (anyAdmin || isAuditor) allowed.push("parameters");

    // Criteria - same visibility as parameters (lives inside parameter settings)
    if (anyAdmin || isAuditor) allowed.push("criteria");

    // Audit Categories - admin / super / p3m / owner
    if (anyAdmin || isOwner) allowed.push("auditCategories");

    // Organizations - p3mSuperAdmin only
    if (isP3mSuperAdmin) allowed.push("organizations");

    // Template Marketplace - any authenticated user can browse; results are
    // scoped server-side to global templates plus the user's org templates.
    allowed.push("templates");

    // Departments - admin / owner / super / p3m
    if (anyAdmin || isOwner) allowed.push("departments");

    // Posts (Socials) - any authenticated user; backend already org-scopes results
    allowed.push("posts");

    // Tickets - admins see all tickets matching the query; non-admins still
    // get this type and the backend scopes results to tickets they raised or
    // commented on.
    allowed.push("tickets");

    // One-Time Audits - /one-time-audits is ProtectedRoute only (any
    // authenticated user); backend scopes results to the user's organization.
    allowed.push("oneTimeAudits");

    // Question Banks, Notices, Students - /question-banks/manage, /notice and
    // /evaluation-students are all AdminRoute-protected.
    if (anyAdmin) {
        allowed.push("questionBanks");
        allowed.push("notices");
        allowed.push("students");
    }

    return allowed;
}

export const ENTITY_LABELS: Record<SearchEntityType, string> = {
    audits: "Audits",
    assessments: "Assessments",
    evaluations: "Evaluations",
    anecdotals: "Anecdotal Records",
    tasks: "Tasks",
    imageVerification: "Image Verification",
    people: "People",
    groups: "Groups",
    parameters: "Parameters",
    criteria: "Criteria",
    auditCategories: "Audit Categories",
    organizations: "Organizations",
    templates: "Template Marketplace",
    departments: "Departments",
    posts: "Posts",
    tickets: "Tickets",
    notices: "Notices",
    questionBanks: "Question Banks",
    students: "Students",
    oneTimeAudits: "One-Time Audits",
};

export const ENTITY_LIST_URL: Record<SearchEntityType, string> = {
    audits: "/audits",
    assessments: "/assessments",
    evaluations: "/evaluations",
    anecdotals: "/anecdotal-records",
    tasks: "/task-and-goals/tasks",
    imageVerification: "/image-verification",
    people: "/all-employees",
    groups: "/groups",
    parameters: "/parameter-settings",
    criteria: "/parameter-settings",
    auditCategories: "/parameter-settings",
    organizations: "/add-organization",
    templates: "/template-marketplace",
    departments: "/department-and-unit",
    posts: "/socials",
    tickets: "/tickets",
    notices: "/notice",
    questionBanks: "/question-banks/manage",
    students: "/evaluation-students",
    oneTimeAudits: "/one-time-audits",
};
